/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { setPlayerTeam, addPriority } from 'redux/slices';
import { useSelector, useDispatch } from 'react-redux';
import { selectNextTeam } from '../utils/suggestTeam';

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Quem do time que entra cede a vaga quando jogadores do time em quadra são movidos para ele.
// Só cede o necessário para não passar de `vacancy`; prefere quem atende `prefer` e,
// entre eles, quem entrou por último na seleção.
const pickPlayersGivingUp = (selectedPlayers, movedCount, vacancy, prefer) => {
  const needed = Math.max(0, selectedPlayers.length + movedCount - vacancy);
  const preferred = selectedPlayers.filter(prefer).reverse();
  const others = selectedPlayers.filter((p) => !prefer(p)).reverse();
  return [...preferred, ...others].slice(0, needed);
};

// teamId: time que o botão preenche (1 = A, 2 = B). Sem teamId, preenche o time com menos jogadores.
export function GenerateBalancedTeamBtn({ label = 'Próximos jogadores', teamId } = {}) {
  const players = useSelector((state) => state.players);
  const playersPerTeam = useSelector((state) => state.game.playersPerTeam);
  const genderBalanceMode = useSelector((state) => state.game.genderBalanceMode);
  const genderRestrictedMode = useSelector((state) => state.game.genderRestrictedMode);
  const femalesPerTeam = useSelector((state) => state.game.femalesPerTeam);
  const setterRestrictedMode = useSelector((state) => state.game.setterRestrictedMode);
  const autoBalanceMode = useSelector((state) => state.game.autoBalanceMode);
  const dispatch = useDispatch();

  const [adjustmentModal, setAdjustmentModal] = useState(null);

  const generateTeam = () => {
    const {
      targetTeamId, selectedPlayers, priorityPlayers, playersMissing, opposingTeam, playersWaiting,
    } = selectNextTeam({
      players,
      playersPerTeam,
      targetTeamId: teamId,
      genderBalanceMode,
      genderRestrictedMode,
      femalesPerTeam,
      setterRestrictedMode,
      autoBalanceMode,
      shuffle: shuffleArray,
    });
    const opposingTeamId = targetTeamId === 1 ? 2 : 1;
    const selectedIds = selectedPlayers.map((p) => p.playerId);

    // Verificar ajustes de gênero nos times
    if (genderBalanceMode && genderRestrictedMode) {
      const opposingFemaleCount = opposingTeam.filter((p) => p.gender === 'F').length;
      const femalesMissingInOpposing = femalesPerTeam - opposingFemaleCount;
      const femalesExcessInOpposing = opposingFemaleCount - femalesPerTeam;

      // Caso 1: Time vencedor precisa de mulher
      if (femalesMissingInOpposing > 0) {
        const femalesInQueue = playersWaiting.filter(
          (p) => p.gender === 'F' && !selectedIds.includes(p.playerId),
        );
        const opposingMales = opposingTeam.filter((p) => p.gender !== 'F');
        const swappableMales = setterRestrictedMode
          ? opposingMales.filter((p) => p.playerType !== 'Setter')
          : opposingMales;

        if (femalesInQueue.length > 0 && swappableMales.length > 0) {
          const femalesToAdd = femalesInQueue.slice(0, Math.min(femalesMissingInOpposing, swappableMales.length));
          const malesToMove = swappableMales.slice(0, femalesToAdd.length);
          setAdjustmentModal({
            type: 'addFemaleToWinner',
            selectedPlayers,
            targetTeamId,
            opposingTeamId,
            femalesToAdd,
            malesToMove,
            playersGivingUp: pickPlayersGivingUp(selectedPlayers, malesToMove.length, playersMissing, (p) => p.gender !== 'F'),
            queueCompensation: priorityPlayers,
          });
          return;
        }
      }

      // Caso 2: Time vencedor tem mulheres a mais (configuração diminuiu)
      if (femalesExcessInOpposing > 0) {
        // Cada mulher que sai precisa de um homem da fila no lugar (senão o time vencedor fica desfalcado).
        const malesInQueue = playersWaiting.filter(
          (p) => p.gender !== 'F' && !selectedIds.includes(p.playerId),
        );
        const count = Math.min(femalesExcessInOpposing, malesInQueue.length);

        if (count > 0) {
          const femalesToMove = opposingTeam.filter((p) => p.gender === 'F').slice(-count);
          setAdjustmentModal({
            type: 'removeFemaleFromWinner',
            selectedPlayers,
            targetTeamId,
            opposingTeamId,
            femalesToMove,
            malesToAddToWinner: malesInQueue.slice(0, count),
            playersGivingUp: pickPlayersGivingUp(selectedPlayers, count, playersMissing, (p) => p.gender === 'F'),
            queueCompensation: priorityPlayers,
          });
          return;
        }
      }
    }

    selectedPlayers.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: targetTeamId }));
    });

    // Dar prioridade aos jogadores que cederam lugar para quem furou a fila
    priorityPlayers.forEach((player) => {
      dispatch(addPriority(player.playerId));
    });
  };

  const giveQueueCompensation = () => {
    (adjustmentModal.queueCompensation || []).forEach((player) => {
      dispatch(addPriority(player.playerId));
    });
  };

  const handleConfirmAdjustment = () => {
    if (!adjustmentModal) return;

    const givingUpIds = (adjustmentModal.playersGivingUp || []).map((p) => p.playerId);
    // Jogadores selecionados que entram no time que entra (menos os que cedem lugar)
    adjustmentModal.selectedPlayers
      .filter((p) => !givingUpIds.includes(p.playerId))
      .forEach((player) => {
        dispatch(setPlayerTeam({ playerId: player.playerId, teamId: adjustmentModal.targetTeamId }));
      });

    if (adjustmentModal.type === 'addFemaleToWinner') {
      // Homens do time vencedor vão para o time que entra; mulheres da fila vão para o vencedor
      adjustmentModal.malesToMove.forEach((male) => {
        dispatch(setPlayerTeam({ playerId: male.playerId, teamId: adjustmentModal.targetTeamId }));
      });
      adjustmentModal.femalesToAdd.forEach((female) => {
        dispatch(setPlayerTeam({ playerId: female.playerId, teamId: adjustmentModal.opposingTeamId }));
      });
    } else if (adjustmentModal.type === 'removeFemaleFromWinner') {
      // Mulheres do time vencedor vão para o time que entra; homens da fila vão para o vencedor
      adjustmentModal.femalesToMove.forEach((female) => {
        dispatch(setPlayerTeam({ playerId: female.playerId, teamId: adjustmentModal.targetTeamId }));
      });
      adjustmentModal.malesToAddToWinner.forEach((male) => {
        dispatch(setPlayerTeam({ playerId: male.playerId, teamId: adjustmentModal.opposingTeamId }));
      });
    }

    // Prioridade para quem cedeu lugar no ajuste e para quem foi passado para trás na fila
    givingUpIds.forEach((playerId) => dispatch(addPriority(playerId)));
    giveQueueCompensation();

    setAdjustmentModal(null);
  };

  const handleIgnoreAdjustment = () => {
    if (!adjustmentModal) return;

    // Apenas adicionar jogadores selecionados ao time perdedor, sem ajustes
    adjustmentModal.selectedPlayers.forEach((player) => {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId: adjustmentModal.targetTeamId }));
    });
    giveQueueCompensation();

    setAdjustmentModal(null);
  };

  return (
    <>
      <Button variant="contained" onClick={() => generateTeam()}>
        {label}
      </Button>

      <Dialog open={!!adjustmentModal} onClose={() => setAdjustmentModal(null)}>
        <DialogTitle>Ajustar times</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {adjustmentModal?.type === 'addFemaleToWinner' && (
              <>
                O Time
                {' '}
                <strong>{adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}</strong>
                {' '}
                precisa de
                {' '}
                <strong>{adjustmentModal?.femalesToAdd?.length}</strong>
                {' '}
                mulher(es) para atender a configuração de
                {' '}
                <strong>{femalesPerTeam}</strong>
                {' '}
                mulher(es) por time.
              </>
            )}
            {adjustmentModal?.type === 'removeFemaleFromWinner' && (
              <>
                O Time
                {' '}
                <strong>{adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}</strong>
                {' '}
                tem
                {' '}
                <strong>
                  {adjustmentModal?.femalesToMove?.length || 0}
                </strong>
                {' '}
                mulher(es) a mais para a configuração de
                {' '}
                <strong>{femalesPerTeam}</strong>
                {' '}
                mulher(es) por time.
              </>
            )}
            <br />
            <br />
            <strong>Ajuste sugerido:</strong>
            <br />
            {adjustmentModal?.malesToMove?.map((male) => (
              <span key={male.playerId}>
                •
                {' '}
                <strong>{male.playerName}</strong>
                {' '}
                sai do Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                {' '}
                e vai para o Time
                {' '}
                {adjustmentModal?.targetTeamId === 1 ? 'A' : 'B'}
                <br />
              </span>
            ))}
            {adjustmentModal?.femalesToAdd?.map((female) => (
              <span key={female.playerId}>
                •
                {' '}
                <strong>{female.playerName}</strong>
                {' '}
                entra no Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                <br />
              </span>
            ))}
            {adjustmentModal?.femalesToMove?.map((female) => (
              <span key={female.playerId}>
                •
                {' '}
                <strong>{female.playerName}</strong>
                {' '}
                sai do Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                {' '}
                e vai para o Time
                {' '}
                {adjustmentModal?.targetTeamId === 1 ? 'A' : 'B'}
                <br />
              </span>
            ))}
            {adjustmentModal?.malesToAddToWinner?.map((male) => (
              <span key={male.playerId}>
                •
                {' '}
                <strong>{male.playerName}</strong>
                {' '}
                entra no Time
                {' '}
                {adjustmentModal?.opposingTeamId === 1 ? 'A' : 'B'}
                {' '}
                (da fila)
                <br />
              </span>
            ))}
            {adjustmentModal?.playersGivingUp?.map((player) => (
              <span key={player.playerId}>
                •
                {' '}
                <strong>{player.playerName}</strong>
                {' '}
                cede lugar e recebe prioridade (P)
                <br />
              </span>
            ))}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIgnoreAdjustment}>Ignorar</Button>
          <Button variant="contained" onClick={handleConfirmAdjustment}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
